import React from 'react';
import { FiMoreVertical, FiPause, FiPlay, FiTrash2 } from 'react-icons/fi';

import { Button } from '../../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import IconWrapper from '../../../ui/IconWrapper';
import type { ImportJob } from '../../../../types/importJobs';

interface ImportJobActionsProps {
  job: ImportJob;
  onPause: (job: ImportJob) => void;
  onResume: (job: ImportJob) => void;
  onDelete: (job: ImportJob) => void;
  disabled?: boolean;
}

/**
 * Row action menu for a single monitor on the Monitor runs page.
 *
 * Pause / Resume toggle on `job.status`; Delete is destructive and removes
 * the monitor plus its run history. Run-now / Edit are intentionally absent —
 * run-now is a deferred no-op on the backend, and editing happens through the
 * wizard, not this read-history surface.
 */
const ImportJobActions: React.FC<ImportJobActionsProps> = ({
  job,
  onPause,
  onResume,
  onDelete,
  disabled = false,
}) => {
  const isPaused = job.status === 'paused';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Actions for ${job.name}`}
          disabled={disabled}
          data-testid="import-job-actions-trigger"
          // Stop the row's expand/collapse toggle from also firing.
          onClick={(event) => event.stopPropagation()}
        >
          <IconWrapper icon={FiMoreVertical} className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {isPaused ? (
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              onResume(job);
            }}
            data-testid="import-job-action-resume"
          >
            <IconWrapper icon={FiPlay} className="mr-2 h-4 w-4" />
            Resume
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              onPause(job);
            }}
            data-testid="import-job-action-pause"
          >
            <IconWrapper icon={FiPause} className="mr-2 h-4 w-4" />
            Pause
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          variant="destructive"
          onSelect={(event) => {
            event.preventDefault();
            onDelete(job);
          }}
          data-testid="import-job-action-delete"
        >
          <IconWrapper icon={FiTrash2} className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ImportJobActions;
